import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CompanyLayerField } from '@/services/workspace.service';

/**
 * The values of a feature just drawn (or selected), one input per field of the layer the person may see, typed as the
 * field is: whole numbers, decimals, yes/no, dates, text. What the server still refuses (a mandatory field left
 * empty, ...) it says on saving, field by field.
 *
 * Values are by the names the map's features carry them under (a field's column), as the session keeps them.
 */

export type Values = Record<string, unknown>;

const input = 'w-full px-2 py-1 border border-gn-input rounded bg-gn-input text-gn-primary text-xs';

function kindOf(type: string): 'integer' | 'decimal' | 'boolean' | 'date' | 'timestamp' | 'text' {
  switch (type) {
    case 'integer': return 'integer';
    case 'decimal': case 'money': case 'quantity': return 'decimal';
    case 'boolean': return 'boolean';
    case 'date': return 'date';
    case 'timestamp': return 'timestamp';
    default: return 'text';
  }
}

/** The text an input shows for a value. */
function shown(v: unknown, kind: ReturnType<typeof kindOf>): string {
  if (v === null || v === undefined) return '';
  if (kind === 'boolean') return v === true || v === 'true' ? 'true' : v === false || v === 'false' ? 'false' : '';
  if (kind === 'timestamp' && typeof v === 'string') return v.substring(0, 16);
  return String(v);
}

/** The value an input's text is, or an error key. */
export function parseValue(text: string, type: string): { value: unknown } | { error: string } {
  const kind = kindOf(type);
  const t = text.trim();
  if (t === '') return { value: null };
  switch (kind) {
    case 'integer': {
      if (!/^-?\d+$/.test(t)) return { error: 'editing.form.not_integer' };
      return { value: Number.parseInt(t, 10) };
    }
    case 'decimal': {
      const n = Number(t.replace(',', '.'));
      if (!Number.isFinite(n)) return { error: 'editing.form.not_number' };
      return { value: n };
    }
    case 'boolean':
      return { value: t === 'true' };
    case 'timestamp':
      return { value: t.length === 16 ? `${t}:00` : t };
    default:
      return { value: t };
  }
}

export const AttributeForm: React.FC<{
  fields: CompanyLayerField[];
  initial: Values;
  isNew: boolean;
  lang: string;
  onDone: (values: Values | null) => void;
}> = ({ fields, initial, isNew, lang, onDone }) => {
  const { t } = useTranslation();
  const visible = useMemo(() => fields.filter((f) => !f.hidden), [fields]);
  const [texts, setTexts] = useState<Record<string, string>>(() =>
    Object.fromEntries(visible.map((f) => [f.column, shown(initial[f.column], kindOf(f.type))])));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const first = useRef<HTMLInputElement | HTMLSelectElement | null>(null);

  useEffect(() => { first.current?.focus(); }, []);

  const submit = (e?: React.FormEvent) => {
    e?.preventDefault();
    const out: Values = {};
    const wrong: Record<string, string> = {};
    for (const f of visible) {
      const r = parseValue(texts[f.column] ?? '', f.type);
      if ('error' in r) wrong[f.column] = r.error;
      else out[f.column] = r.value;
    }
    setErrors(wrong);
    if (Object.keys(wrong).length === 0) onDone(out);
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/30" role="dialog" aria-modal="true"
         data-testid="editing-attributes" onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); onDone(null); } }}>
      <form onSubmit={submit} className="w-80 max-h-[80vh] flex flex-col rounded-lg border border-gn-light bg-gn-modal shadow-gn-lg">
        <div className="px-3 py-2 border-b border-gn-light text-sm font-medium text-gn-primary">
          {isNew ? t('editing.form.title_new') : t('editing.form.title_edit')}
        </div>
        <div className="px-3 py-2 space-y-2 overflow-y-auto">
          {visible.length === 0 && <p className="text-xs text-gn-muted">{t('editing.form.no_fields')}</p>}
          {visible.map((f, i) => {
            const kind = kindOf(f.type);
            const label = f.labels?.[lang] || f.labels?.en || f.column;
            const common = {
              id: `edit-field-${f.key}`,
              name: f.column,
              className: input,
              value: texts[f.column] ?? '',
              ref: i === 0 ? (el: any) => { first.current = el; } : undefined,
            };
            return (
              <div key={f.key}>
                <label htmlFor={common.id} className="block text-[11px] text-gn-secondary mb-0.5">{label}</label>
                {kind === 'boolean' ? (
                  <select {...common} onChange={(e) => setTexts({ ...texts, [f.column]: e.target.value })}>
                    <option value="">—</option>
                    <option value="true">{t('editing.form.yes')}</option>
                    <option value="false">{t('editing.form.no')}</option>
                  </select>
                ) : (
                  <input {...common}
                         type={kind === 'integer' || kind === 'decimal' ? 'text' : kind === 'date' ? 'date' : kind === 'timestamp' ? 'datetime-local' : 'text'}
                         inputMode={kind === 'integer' ? 'numeric' : kind === 'decimal' ? 'decimal' : undefined}
                         onChange={(e) => setTexts({ ...texts, [f.column]: e.target.value })} />
                )}
                {errors[f.column] && <p className="text-[10px] text-gn-danger mt-0.5">{t(errors[f.column])}</p>}
              </div>
            );
          })}
        </div>
        <div className="flex justify-end gap-2 px-3 py-2 border-t border-gn-light">
          <button type="button" className="px-3 py-1 text-xs rounded border border-gn-light text-gn-secondary hover:bg-gn-hover"
                  onClick={() => onDone(null)}>
            {isNew ? t('editing.form.discard_feature') : t('editing.cancel')}
          </button>
          <button type="submit" className="px-3 py-1 text-xs rounded bg-gn-blue text-gn-inverse" data-testid="editing-attributes-ok">
            {t('editing.form.ok')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AttributeForm;
